import Card from 'assets/components/card/Card';
import ThemeCardListItem from 'assets/components/card/themes/Theme.Card.ListItem';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { isNil } from 'lodash';
import React from 'react';

type TableData = Api.Sales.ContractRefund.RefundPricingInfo.RefundPriceInfo & {
  excludeAdministrationFee?: boolean;
};
type RowType = 'dealerCost' | 'dealerProfit' | 'retailSalePrice';
type TableProps = {
  data: TableData;
  actions?: Partial<Record<RowType, React.ReactNode>>;
  onAdminFeeExclusion?(data: { excludeAdministrationFee: TableData['excludeAdministrationFee'] }): void;
};

const formatCurrency = (value?: number) => (isNil(value) ? 'N/A' : `$${formatNumberWithSeparator(value)}`);

export const ContractRefundSummariesTable = ({ data, actions, onAdminFeeExclusion }: TableProps) => {
  const { lang } = getLocales();
  const protectedAmount = data.refundConfigTemplate?.dealerProtectProfitAmount;
  const dealerProtectProfitExpirationDays = data.refundConfigTemplate?.dealerProtectProfitExpirationDays;
  const shouldShowDealerProfitSplit =
    !isNil(protectedAmount) &&
    !isNil(dealerProtectProfitExpirationDays) &&
    data.usedTermInDays >= dealerProtectProfitExpirationDays;
  const dealerProfitAfterProtection = isNil(protectedAmount)
    ? undefined
    : Math.max(data.dealerProfitValueAtSale - protectedAmount, 0);
  const proratedRetainedAmount = isNil(dealerProfitAfterProtection)
    ? undefined
    : dealerProfitAfterProtection * data.retainFactor;
  const dealerProfitRetainedSplitDisplay =
    shouldShowDealerProfitSplit && !isNil(proratedRetainedAmount)
      ? `${formatCurrency(protectedAmount)} + ${formatCurrency(proratedRetainedAmount)}`
      : undefined;

  const renderLabel = (rowType: RowType) => {
    const action = actions?.[rowType];
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{lang[rowType]}</span>
        {action ? <span style={{ marginLeft: '8px', display: 'inline-flex', gap: '6px' }}>{action}</span> : null}
      </div>
    );
  };

  return (
    <Card
      subtitle={() => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ alignSelf: 'flex-start' }}>{lang.contractSummaryAmounts}</p>
              {onAdminFeeExclusion && (
                <CheckWrapper style={{ display: 'flex', gap: Theme.Size.XXS }}>
                  <CheckInput
                    name="excludeAdministrationFee"
                    label={lang.removeAdminFee}
                    class={inputStyles.check.switch}
                    value={data.excludeAdministrationFee}
                    onChange={onAdminFeeExclusion}
                    htmlElementProps={{ title: data.excludeAdministrationFee ? lang.disable : lang.enable }}
                    style={{ fontWeight: 'bold' }}
                  />
                </CheckWrapper>
              )}
            </div>
            &nbsp;
            <table className={ThemeTableSimple}>
              <thead>
                <tr>
                  <th></th>
                  <th style={{ textAlign: 'center' }}>{lang.valueAtSales}</th>
                  <th style={{ textAlign: 'center' }}>{lang.valueRetained}</th>
                  <th style={{ textAlign: 'center' }}>{lang.refundAmount}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{renderLabel('dealerCost')}</td>
                  <td style={{ textAlign: 'center' }}>${data?.dealerCostValueAtSale ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>${data?.retainedDealerCostTotal ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>${data?.refundedDealerCostTotal ?? 'N/A'}</td>
                </tr>
                <tr style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{renderLabel('dealerProfit')}</td>
                  <td style={{ textAlign: 'center' }}>${data?.dealerProfitValueAtSale ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {dealerProfitRetainedSplitDisplay ?? formatCurrency(data?.retainedDealerProfitTotal)}
                  </td>
                  <td style={{ textAlign: 'center' }}>${data?.refundedDealerProfitTotal ?? 'N/A'}</td>
                </tr>
                <tr style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{renderLabel('retailSalePrice')}</td>
                  <td style={{ textAlign: 'center' }}>${data?.retailSalePriceValueAtSale ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>${data?.retainedDealerTotal ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>${data?.refundedDealerTotal ?? 'N/A'}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 'bold' }} colSpan={4}>
                    {lang.totalDeductions}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'normal' }}>{lang.administrationFee}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'normal' }} colSpan={3}>
                    {data?.excludeAdministrationFee ? lang.excluded : `$${data?.totalDeductions ?? 0}`}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'normal' }}>{lang.administrationFeeTax}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'normal' }} colSpan={3}>
                    {data?.excludeAdministrationFee ? lang.excluded : `$${data?.totalDeductionsTaxAmount ?? 0}`}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'normal' }}>{lang.sumOfClaimAmountsPaidOnContract}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'normal' }} colSpan={3}>
                    ${data?.sumOfClaimAmountsPaidOnContract ?? 0}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>{lang.totalToBeRefunded}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'bold' }} colSpan={3}>
                    ${data?.totalToBeRefunded ?? 0}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      }}
      class={ThemeCardListItem}
      style={{
        borderRadius: Theme.Size.S,
        cardMedia: {
          width: 0,
        },
        cardInfo: {
          paddingRight: Theme.Size.M,
        },
      }}
    />
  );
};
